import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';

import Alert from '@/core/components/Alert';
import Header from '@/core/components/Header';
import Icons from '@/core/components/Icons';
import ProfileActions from '@/core/components/ProfileActions';
import ProfileDetails from '@/core/components/ProfileDetails';
import { ProfileTitle } from '@/core/components/ProfileDetails/components';
import useDisclosure from '@/core/hooks/useDisclosure';
import { useComponentsContext } from '@/core/lib/components/components.context';
import { useFF } from '@/core/lib/ff/ff.context';
import { useNativeContext } from '@/core/lib/native/native.context';
import Store from '@/core/lib/new-architecture/store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { PageWithLayoutsAndProviders } from '@/core/pages/page.types';
import { DonationList } from '@/core/pages/profile/PublicProfile/DonationList';
import { StatusAndBadges } from '@/core/pages/profile/PublicProfile/Status/StatusAndBadges';
import { FeatureFlag } from '@/core/types/featureFlag.type';

dayjs.extend(utc);
dayjs.locale('fr');

const PublicProfile: PageWithLayoutsAndProviders = () => {
  const { t } = useTranslationContext(['common', 'profile.profile-public']);
  const { back, query, onGlobalNotFound } = useRouterContext();
  const { Page, Content } = useComponentsContext();
  const { isNative } = useNativeContext();
  const { isFFactivate: hasFFSuperUser } = useFF(FeatureFlag.SUPER_USER);

  const onBack = () => back(new Routes.ProfileRoute({}));

  const { data: user, isLoading, errors } = Store.user.usePublicProfile(query.uuid);

  const donations = user?.getDonations() ?? [];
  const actions = user?.getActions() ?? [];

  const { isOpen: isActionsOpen, onOpen: onOpenActions, onClose: onCloseActions } = useDisclosure();

  const isPrivate = user?.isPrivate();

  const navbarContent = t(`nav-content-${isPrivate ? 'private' : 'public'}`, { ns: 'profile.profile-public' });

  if (!isLoading && !user) {
    onGlobalNotFound?.();
    return;
  }

  return (
    <Page noPadding={isNative}>
      <Content>
        <main className="bg-bg-primary">
          <div className="relative">
            <Header
              content={navbarContent}
              iconBefore="arrow-left"
              onBefore={onBack}
              iconAfter={actions.length > 0 ? 'more-vertical' : undefined}
              onAfter={actions.length > 0 ? onOpenActions : undefined}
              hasPadding
            />
            <ProfileActions actions={actions} isOpen={isActionsOpen} onClose={onCloseActions} />
          </div>

          {isLoading && (
            <div className="flex size-full items-center justify-center">
              <Icons icon="spinner" size="40" color="button-default" />
            </div>
          )}

          {!!errors && <Alert status="error">{t('errors.unknown', { ns: 'common' })}</Alert>}

          {!isLoading && user && (
            <div className="m-8 space-y-5 lg:space-y-6">
              <ProfileDetails user={user} canShare />
              {isPrivate && hasFFSuperUser && <StatusAndBadges user={user} />}
            </div>
          )}
          {!isLoading && donations.length > 0 && (
            <section className="m-8 lg:my-7" aria-label={t('donations.aria-label', { ns: 'profile.profile-public' })}>
              <ProfileTitle tag="h2">{t('components.profile.public.donations.donationsList', { ns: 'common', count: donations.length })}</ProfileTitle>
              <div className="mt-4 lg:mt-7">
                <DonationList donations={donations} />
              </div>
            </section>
          )}
          {!isLoading && donations.length === 0 && (
            <div className="flex w-full justify-center px-4">
              <div className="flex h-full w-100 flex-col items-center justify-items-start">
                <img src="/assets/empty/profile-donations-current.svg" className="size-45" alt={t('components.profile.public.empty-donations', { ns: 'common' })} />
                <h1 className="text-body-secondary mb-9 text-center font-semibold text-content-primary">{t('components.profile.public.empty-donations', { ns: 'common' })}</h1>
              </div>
            </div>
          )}
        </main>
      </Content>
    </Page>
  );
};

export default PublicProfile;
